/**
 * Texts of screen E7 · Channel settings (specs/M2-channel-settings/spec.md §4; mockups Reglages,
 * ReglagesSansSL).
 *
 * Provisional until Q3 (tone; French uses "tu", as in the mockups). State texts that are reason
 * codes (CHANNEL_PAUSED, RISK_PREVIEW_UNAVAILABLE, …) live in the C2 catalogue, not here.
 */
export const settingsFr = {
  title: 'Réglages du canal',
  back: 'Retour',
  subtitle: 'Réglages de ce canal uniquement',
  loading: 'Chargement…',
  retry: 'Réessayer',
  chip: { paused: 'En pause', suspended: 'Suspendu' },
  firstSetup: {
    confirm: 'Confirmer et lancer la copie',
    prefilled:
      '<strong>Pré-rempli avec le réglage conseillé par ton affilié</strong> : {{sizing}}, {{execution}}. Modifie ce que tu veux avant de confirmer.',
    // P8 R5 (Proposal, to be validated): the recommended mode is hidden, so it is not named.
    prefilledSizeOnly:
      '<strong>Pré-rempli avec le réglage conseillé par ton affilié</strong> : {{sizing}}. Choisis le mode d’exécution, puis modifie ce que tu veux avant de confirmer.',
  },
  recommendation: {
    text: '<strong>Recommandé par ton affilié</strong> : {{sizing}}, {{execution}}. Tu peux le modifier.',
    // P8 R5 (Proposal, to be validated): the recommended mode is hidden, so it is not named.
    textSizeOnly: '<strong>Recommandé par ton affilié</strong> : {{sizing}}. Tu peux le modifier.',
    apply: 'Appliquer',
  },
  sizing: {
    title: 'Taille de position',
    riskPercent: 'Pourcentage de risque',
    recommended: 'Recommandé',
    riskHint:
      'La taille est calculée pour que tu perdes ce pourcentage de ton capital si le stop loss est touché.',
    riskLabel: 'Risque par trade (%)',
    fixedLot: 'Lot fixe',
    fixedLotHint: 'La même taille sur chaque trade, quel que soit le signal.',
    lotsLabel: 'Lots par trade',
    summaryRisk: 'risque {{value}} %',
    summaryLots: 'lot fixe {{value}}',
  },
  preview: {
    text: 'Avec ce réglage, un trade avec un stop loss à {{pips}} pips te ferait perdre environ <strong>{{amount}}</strong>.',
    referenceChannel: 'Exemple sur {{symbol}}, l’instrument que ce canal trade le plus.',
    referenceDefault: 'Exemple sur {{symbol}} (ce canal n’a encore aucun signal).',
    referenceNotPriced:
      'Exemple sur {{symbol}}\u00a0: FXMate ne lit pas encore de prix pour {{channelSymbol}} sur ton compte.',
    referenceChannelRisk:
      'Exemple sur {{symbol}}, l’instrument que ce canal trade le plus\u00a0; en risque\u00a0%, le montant est le même sur tout instrument.',
    referenceDefaultRisk:
      'Exemple sur {{symbol}} (ce canal n’a encore aucun signal)\u00a0; en risque\u00a0%, le montant est le même sur tout instrument.',
    referenceNotPricedRisk:
      'Exemple sur {{symbol}}\u00a0: FXMate ne lit pas encore de prix pour {{channelSymbol}} sur ton compte\u00a0; en risque\u00a0%, le montant est le même sur tout instrument.',
    idle: 'Choisis une taille pour voir ce qu’un trade peut te coûter.',
    loading: 'Calcul de ton risque…',
  },
  withoutStopLoss: {
    title: 'Signaux sans stop loss',
    label: 'Copier aussi les signaux sans stop loss',
    off: 'Désactivé : ces signaux sont ignorés et tu reçois un message pour chacun.',
    on: 'Activé : ces signaux seront copiés.',
    warning:
      'Une position sans stop loss peut perdre sans limite : tu devras la surveiller toi-même.',
    fallbackIntro:
      'En mode risque, FXMate a besoin d’une distance de stop loss pour calculer la taille.',
    fallbackLabel: 'Stop loss de secours (pips)',
    fallbackHelp: 'Elle sert uniquement à calculer la taille ; aucun stop loss n’est placé.',
    fallbackRequired: 'Saisis une distance pour pouvoir enregistrer.',
    fallbackExample:
      'Sans stop, FXMate calcule la taille comme si le stop était à {{pips}} pips : si le prix va de {{pips}} pips contre toi, tu perds environ <strong>{{amount}}</strong>, soit ton risque par trade. Au-delà, la perte continue de grandir.',
  },
  execution: {
    title: 'Mode d’exécution',
    automatic: 'Automatique',
    automaticHint: 'Les signaux sont copiés dès leur arrivée.',
    confirmation: 'Confirmation',
    confirmationHint:
      'Le bot te demande de confirmer chaque signal. Sans réponse après <num>{{minutes}}</num> min, le trade n’est pas pris.',
    followUps:
      'Les messages de suivi (stop loss à BE, prise de profit partielle, clôture) sont toujours appliqués automatiquement.',
    summaryAutomatic: 'automatique',
    summaryConfirmation: 'confirmation',
    // P8 R3 (Proposal, to be validated; wording asked in Q257).
    confirmationUnavailable:
      'Ce canal est en mode confirmation, mais les demandes de confirmation ne sont pas encore envoyées : ses signaux attendent sans être copiés. Choisis le mode automatique ci-dessous puis enregistre, ou mets ce canal en pause.',
  },
  limits: {
    title: 'Limites de sécurité · recommandées',
    maxPositions: 'Max. de positions ouvertes sur ce canal',
    maxPositionsLabel: 'Nombre maximal de positions',
    dailyLoss: 'Perte max. par jour',
    dailyLossScope:
      'Pour tout le compte, trades manuels compris. Une fois atteinte, aucune nouvelle position ne s’ouvre jusqu’à minuit (heure de Paris). Les clôtures continuent.',
    allChannels: 'Cette limite s’applique à tous tes canaux.',
    // M1 R20, M2 AC20 (Proposal, to be validated): reachable account, no reading of today's loss.
    dailyLossNotAvailable:
      'Perte du jour indisponible pour le moment : la limite ne peut pas être modifiée maintenant. Si une limite est en vigueur, les nouvelles ouvertures peuvent être refusées tant que la perte reste inconnue.',
    amountLabel: 'Perte maximale (% du capital)',
    currentPercent: 'Limite actuelle : <num>{{value}}</num> du capital.',
    keepCurrent: 'Laisse vide pour garder la limite actuelle.',
  },
  errors: {
    sizingMode: 'Choisis une taille de position.',
    executionMode: 'Choisis un mode d’exécution.',
    riskPercent: 'Saisis un pourcentage supérieur à 0 et jusqu’à 100, avec 2 décimales au plus.',
    lots: 'Saisis un lot supérieur à 0, avec 2 décimales au plus.',
    fallbackStopLossPips: 'Saisis un nombre entier de pips entre 1 et 10 000.',
    maxOpenPositions: 'Saisis un nombre entier, au moins 1.',
    dailyLoss: 'Saisis un pourcentage de 0,01 à 100, avec 2 décimales au plus.',
    previewPending: 'Attends le calcul de ton risque.',
    unchanged: 'Modifie un réglage pour enregistrer une nouvelle version.',
    leadingDigit: 'Ajoute un chiffre avant la virgule, par exemple 0,5.',
  },
  missingTitle: 'Pour enregistrer :',
  save: 'Enregistrer les réglages',
  saving: 'Enregistrement…',
  saved: {
    next: 'Ces réglages s’appliqueront aux prochains signaux.',
    openPositions: 'Tes positions ouvertes sur ce canal gardent leur taille.',
    openPositionsUnknown: 'Les positions déjà ouvertes sur ce canal gardent leur taille.',
    limitOnly: 'Ta limite de perte journalière est enregistrée.',
  },
  /** P11 · high-risk confirmation (Proposal, to be validated; wording in Q260). */
  highRisk: {
    title: 'Confirmer ce risque ?',
    riskPercent:
      'Tu risques <num>{{amount}}</num> par trade (<num>{{percent}}\u202f%</num> de ton capital).',
    riskPercentNoAmount:
      'Tu risques <num>{{percent}}\u202f%</num> de ton capital par trade. Le montant ne peut pas être calculé pour le moment.',
    fixedLot:
      'Avec <num>{{lots}}</num> lot, un trade avec un stop loss à <num>{{pips}}</num> pips te ferait perdre environ <num>{{amount}}</num>, soit environ <num>{{share}}</num> de ton capital.',
    aboveThreshold:
      'C’est plus de <num>{{threshold}}</num> de ton capital sur un seul trade. Vérifie que c’est bien la valeur voulue.',
    confirm: 'Confirmer et enregistrer',
    cancel: 'Modifier la valeur',
  },
  /** M3 · pause and resume control (M3 §4; mockup 6 "Pause this channel"). */
  pauseResume: {
    pause: 'Mettre ce canal en pause',
    resume: 'Réactiver ce canal',
    pausing: 'Mise en pause…',
    resuming: 'Réactivation…',
    cancelled_one: '{{count}} demande de validation en attente a été annulée.',
    cancelled_other: '{{count}} demandes de validation en attente ont été annulées.',
  },
};

/** Shape of the `settings` section of `Messages`, derived like `HomeMessages`. */
export type SettingsMessages = typeof settingsFr;

/** English texts of screen E7. Provisional until Q3 and Q15 (EN mockups). */
export const settingsEn: SettingsMessages = {
  title: 'Channel settings',
  back: 'Back',
  subtitle: 'Settings for this channel only',
  loading: 'Loading…',
  retry: 'Retry',
  chip: { paused: 'Paused', suspended: 'Suspended' },
  firstSetup: {
    confirm: 'Confirm and start copying',
    prefilled:
      '<strong>Prefilled with the settings recommended by your affiliate</strong>: {{sizing}}, {{execution}}. Change anything you want before confirming.',
    prefilledSizeOnly:
      '<strong>Prefilled with the settings recommended by your affiliate</strong>: {{sizing}}. Choose the execution mode, then change anything you want before confirming.',
  },
  recommendation: {
    text: '<strong>Recommended by your affiliate</strong>: {{sizing}}, {{execution}}. You can change it.',
    textSizeOnly: '<strong>Recommended by your affiliate</strong>: {{sizing}}. You can change it.',
    apply: 'Apply',
  },
  sizing: {
    title: 'Position size',
    riskPercent: 'Risk percentage',
    recommended: 'Recommended',
    riskHint:
      'The size is calculated so you lose this percentage of your capital if the stop loss is hit.',
    riskLabel: 'Risk per trade (%)',
    fixedLot: 'Fixed lot',
    fixedLotHint: 'The same size on every trade, whatever the signal.',
    lotsLabel: 'Lots per trade',
    summaryRisk: 'risk {{value}}%',
    summaryLots: 'fixed lot {{value}}',
  },
  preview: {
    text: 'With this setting, a trade with a {{pips}}-pip stop loss would lose you about <strong>{{amount}}</strong>.',
    referenceChannel: 'Example on {{symbol}}, the instrument this channel trades most.',
    referenceDefault: 'Example on {{symbol}} (this channel has no signal yet).',
    referenceNotPriced:
      'Example on {{symbol}}: FXMate can’t read a price for {{channelSymbol}} on your account yet.',
    referenceChannelRisk:
      'Example on {{symbol}}, the instrument this channel trades most; in risk %, the amount is the same on any instrument.',
    referenceDefaultRisk:
      'Example on {{symbol}} (this channel has no signal yet); in risk %, the amount is the same on any instrument.',
    referenceNotPricedRisk:
      'Example on {{symbol}}: FXMate can’t read a price for {{channelSymbol}} on your account yet; in risk %, the amount is the same on any instrument.',
    idle: 'Choose a size to see what one trade can cost you.',
    loading: 'Calculating your risk…',
  },
  withoutStopLoss: {
    title: 'Signals without stop loss',
    label: 'Also copy signals without a stop loss',
    off: 'Off: these signals are ignored and you get a message for each one.',
    on: 'On: these signals will be copied.',
    warning:
      'A position without a stop loss can lose without limit: you will have to watch it yourself.',
    fallbackIntro: 'In risk mode, FXMate needs a stop loss distance to calculate the size.',
    fallbackLabel: 'Fallback stop loss (pips)',
    fallbackHelp: 'It is only used to calculate the size; no stop loss is placed.',
    fallbackRequired: 'Enter a distance to be able to save.',
    fallbackExample:
      'Without a stop, FXMate sizes as if the stop were {{pips}} pips away: if the price moves {{pips}} pips against you, you lose about <strong>{{amount}}</strong>, your risk per trade. Beyond that, the loss keeps growing.',
  },
  execution: {
    title: 'Execution mode',
    automatic: 'Automatic',
    automaticHint: 'Signals are copied as soon as they arrive.',
    confirmation: 'Confirmation',
    confirmationHint:
      'The bot asks you to confirm each signal. With no reply after <num>{{minutes}}</num> min, the trade is not taken.',
    followUps:
      'Follow-up messages (stop loss to BE, partial take profit, close) are always applied automatically.',
    summaryAutomatic: 'automatic',
    summaryConfirmation: 'confirmation',
    confirmationUnavailable:
      'This channel is in confirmation mode, but confirmation requests are not sent yet: its signals wait without being copied. Choose automatic mode below and save, or pause this channel.',
  },
  limits: {
    title: 'Safety limits · recommended',
    maxPositions: 'Max. open positions on this channel',
    maxPositionsLabel: 'Maximum number of positions',
    dailyLoss: 'Max. loss per day',
    dailyLossScope:
      'For the whole account, manual trades included. Once reached, no new positions open until midnight (Paris time). Closes continue.',
    allChannels: 'This limit applies to all your channels.',
    // M1 R20, M2 AC20 (Proposal, to be validated): reachable account, no reading of today's loss.
    dailyLossNotAvailable:
      'Today’s loss is not available right now: the limit can’t be changed at the moment. If a limit is in force, new openings may be refused until the loss can be read.',
    amountLabel: 'Maximum loss (% of capital)',
    currentPercent: 'Current limit: <num>{{value}}</num> of capital.',
    keepCurrent: 'Leave empty to keep the current limit.',
  },
  errors: {
    sizingMode: 'Choose a position size.',
    executionMode: 'Choose an execution mode.',
    riskPercent: 'Enter a percentage above 0 and up to 100, with at most 2 decimals.',
    lots: 'Enter a lot size above 0, with at most 2 decimals.',
    fallbackStopLossPips: 'Enter a whole number of pips from 1 to 10,000.',
    maxOpenPositions: 'Enter a whole number, at least 1.',
    dailyLoss: 'Enter a percentage from 0.01 to 100, with at most 2 decimals.',
    previewPending: 'Wait for your risk to be calculated.',
    unchanged: 'Change a setting to save a new version.',
    leadingDigit: 'Add a digit before the separator, for example 0.5.',
  },
  missingTitle: 'To save:',
  save: 'Save settings',
  saving: 'Saving…',
  saved: {
    next: 'These settings apply to the next signals.',
    openPositions: 'Your open positions on this channel keep their size.',
    openPositionsUnknown: 'Positions already open on this channel keep their size.',
    limitOnly: 'Your daily loss limit is saved.',
  },
  highRisk: {
    title: 'Confirm this risk?',
    riskPercent:
      'You risk <num>{{amount}}</num> per trade (<num>{{percent}}%</num> of your capital).',
    riskPercentNoAmount:
      "You risk <num>{{percent}}%</num> of your capital per trade. The amount can't be calculated right now.",
    fixedLot:
      'With <num>{{lots}}</num> lots, a trade with a <num>{{pips}}</num>-pip stop loss would lose you about <num>{{amount}}</num>, about <num>{{share}}</num> of your capital.',
    aboveThreshold:
      'That is more than <num>{{threshold}}</num> of your capital on a single trade. Check that this is the value you meant.',
    confirm: 'Confirm and save',
    cancel: 'Change the value',
  },
  pauseResume: {
    pause: 'Pause this channel',
    resume: 'Resume this channel',
    pausing: 'Pausing…',
    resuming: 'Resuming…',
    cancelled_one: '{{count}} pending validation request was cancelled.',
    cancelled_other: '{{count}} pending validation requests were cancelled.',
  },
};
